import { useMemo, useRef } from "react";

import { useDebounce } from "use-debounce";

import {
  INDEX_TYPES,
  OBJECT_TYPES,
  type ObjectType,
  SEARCH_TYPES,
  buildObjectWheres,
  capPerType,
  includeTypes,
  isObjectType,
  isSearchType,
  reachableTypes,
  rowCounts,
  toResults,
  toScopes,
} from "src/components/commandbar/command-index";
import { useReachableNav } from "src/components/layout/nav/use-reachable-nav";
import {
  useCommandBarQuery,
  useRegisterCommandResults,
} from "src/components/commandbar/results-context";
import {
  commandResultTypeLabel,
  scopeLabelMatchStart,
} from "src/components/commandbar/types";
import {
  type CommandBarObjectsQuery,
  useCommandBarObjectsQuery,
} from "src/graphql";
import { useUser } from "src/contexts/user-context";
import { useRealtimeAudiencesEnabledWithoutSuspense } from "src/hooks/use-realtime-audiences-enabled";
import { useChatSessionAgentTypes } from "src/pages/agents/hooks/use-chat-session-agent-types";

// Most recently updated first. At ~110 bytes a row, five full types are ~55KB before gzip.
export const INDEX_LIMIT = 100;
// One quick retry, so a failed request shows its retry row in about a second rather than after the default three.
export const QUERY_RETRIES = 1;
// Every open refetches in the background while the cached rows show at once. Focus doesn't, since an open palette is brief.
export const LIST_QUERY_OPTIONS = {
  staleTime: 0,
  refetchOnWindowFocus: false,
  retry: QUERY_RETRIES,
} as const;
export const SEARCH_DEBOUNCE_MS = 200;
export const SEARCH_MIN_LENGTH = 2;
export const SEARCH_LIMIT = 5;
// A scope on a server-searched type lists its latest rows before anything is typed.
export const SCOPED_LIST_LIMIT = 50;

const selectIndex = (data: CommandBarObjectsQuery) => ({
  results: toResults(data),
  rowCounts: rowCounts(data),
});

// The types the nav shows this viewer: those whose list page it reaches.
export const useReachableResultTypes = () => {
  const { hrefs, isLoading } = useReachableNav();
  return { reachable: reachableTypes(hrefs), isLoading };
};

// Later refetches land on the next keystroke or scope change, so rows never reorder under the highlight.
// Two exceptions: cached rows taken while a fetch was in flight give way once it lands, so a reopen shows fresh rows;
// and nothing is held while the query is failed, so a retry's rows show as soon as it succeeds.
export const useHeldWhileViewing = <T>(
  {
    data,
    isError,
    isFetching = false,
  }: { data: T | undefined; isError: boolean; isFetching?: boolean },
  view: string,
) => {
  const held = useRef({ view, data, isError, isFetching });
  if (
    held.current.view !== view ||
    held.current.data === undefined ||
    held.current.isError ||
    (held.current.isFetching && !isFetching)
  ) {
    held.current = { view, data, isError, isFetching };
  }
  return held.current.data;
};

// Feeds the open palette its objects: a prefetched index matched locally, plus a server search below it.
export const useCommandIndex = () => {
  const { reachable, isLoading: isNavLoading } = useReachableResultTypes();
  // The palette mounts outside the layout's Suspense, so this must not suspend. Queries wait for it so they don't refetch when it lands.
  // They wait for the nav's gates too, so a type it hides while they load never fetches, and one it shows never lands late.
  const realtimeAudiences = useRealtimeAudiencesEnabledWithoutSuspense();
  const includeRealtimeAudiences = realtimeAudiences.enabled;
  const isReady = !realtimeAudiences.isLoading && !isNavLoading;
  const userId = useUser().user?.id;
  const chatAgentTypes = useChatSessionAgentTypes();
  const { search, scope } = useCommandBarQuery();
  const query = search.trim();
  const [term] = useDebounce(query, SEARCH_DEBOUNCE_MS);
  // The scope's brand is part of what's on screen, so a reopen for another brand never keeps the last brand's rows.
  const view = `${query}\n${scope?.type ?? ""}\n${scope?.brandProfileId ?? ""}`;

  const isIndexed = (type: ObjectType) =>
    reachable.has(type) && !isSearchType(type);
  const index = useCommandBarObjectsQuery(
    {
      limit: INDEX_LIMIT,
      ...buildObjectWheres({
        term: "",
        includeRealtimeAudiences,
        userId,
        chatAgentTypes,
      }),
      ...includeTypes(isIndexed),
    },
    {
      ...LIST_QUERY_OPTIONS,
      enabled: isReady && INDEX_TYPES.some(isIndexed),
      select: selectIndex,
    },
  );
  const indexData = useHeldWhileViewing(index, view);

  // A full index page means older rows were left out, so the server searches that type too.
  const isTruncated = (type: ObjectType) =>
    (indexData?.rowCounts.get(type) ?? 0) >= INDEX_LIMIT;
  const searches = (type: ObjectType) =>
    reachable.has(type) &&
    (!scope || scope.type === type) &&
    (isSearchType(type) || isTruncated(type));
  const isSearchable =
    query.length >= SEARCH_MIN_LENGTH && OBJECT_TYPES.some(searches);
  const listedScope =
    scope && isSearchType(scope.type) && searches(scope.type) ? scope : null;
  const serverSearch = useCommandBarObjectsQuery(
    {
      // Inside any scope the server returns a scope's worth, including an indexed type whose index page was full.
      limit:
        scope && isObjectType(scope.type) && searches(scope.type)
          ? SCOPED_LIST_LIMIT
          : SEARCH_LIMIT,
      ...buildObjectWheres({
        term,
        userId,
        chatAgentTypes,
        includeRealtimeAudiences,
        brandProfileId: scope?.brandProfileId,
      }),
      ...includeTypes(searches),
    },
    {
      retry: QUERY_RETRIES,
      enabled: isReady && isSearchable && term.length >= SEARCH_MIN_LENGTH,
      keepPreviousData: true,
      select: toResults,
    },
  );

  // Whether each server-searched type has any rows, so a type with none never offers a scope.
  const isListable = (type: ObjectType) =>
    reachable.has(type) && isSearchType(type);
  const presence = useCommandBarObjectsQuery(
    {
      limit: 1,
      ...buildObjectWheres({
        term: "",
        includeRealtimeAudiences,
        userId,
        chatAgentTypes,
      }),
      ...includeTypes(isListable),
    },
    {
      ...LIST_QUERY_OPTIONS,
      enabled: isReady && SEARCH_TYPES.some(isListable),
      select: rowCounts,
    },
  );
  const presenceCounts = useHeldWhileViewing(presence, view);
  const hasRows = (type: ObjectType) => (presenceCounts?.get(type) ?? 0) > 0;

  // The newest rows of every server-searched type, for their scopes. Fetched once the query offers one of those scopes, so Tab lists them at once.
  const isOffered = (type: ObjectType) =>
    query.length >= SEARCH_MIN_LENGTH &&
    scopeLabelMatchStart(commandResultTypeLabel(type), query) !== -1;
  const listing = useCommandBarObjectsQuery(
    {
      limit: SCOPED_LIST_LIMIT,
      ...buildObjectWheres({
        term: "",
        userId,
        chatAgentTypes,
        includeRealtimeAudiences,
        brandProfileId: scope?.brandProfileId,
      }),
      ...includeTypes(isListable),
    },
    {
      ...LIST_QUERY_OPTIONS,
      enabled:
        isReady &&
        (!!listedScope ||
          (!scope &&
            OBJECT_TYPES.some(
              (type) => isListable(type) && hasRows(type) && isOffered(type),
            ))),
      select: toResults,
    },
  );
  const listingData = useHeldWhileViewing(listing, view);

  const failedSearches = [
    ...(isSearchable && term === query && serverSearch.isError
      ? [serverSearch]
      : []),
    ...(listedScope && listing.isError ? [listing] : []),
  ];
  // A failed presence count only hides scopes, and the next open refetches it, so it never shows the error row.
  const retry = (failed: Array<{ refetch: () => unknown }>) => {
    for (const failedQuery of failed) {
      void failedQuery.refetch();
    }
  };

  // Inside a listed scope the newest rows stay, matched locally, while the server search fills in older ones.
  // Unscoped, each type keeps an unscoped page's worth, so a scope's larger page held over after popping the scope never floods the list.
  // Memoized because the results register with the palette, which re-registers on every new array.
  const serverRows = serverSearch.data;
  const isCapped = isSearchable && !scope;
  const cappedRows = useMemo(
    () =>
      isCapped && serverRows ? capPerType(serverRows, SEARCH_LIMIT) : undefined,
    [isCapped, serverRows],
  );
  const searchResults = isSearchable
    ? ((isCapped ? cappedRows : serverRows) ?? [])
    : [];
  useRegisterCommandResults({
    id: "object-index",
    results: indexData?.results ?? [],
    // A retry counts as loading, so the error row gives way to the skeleton until it settles.
    isLoading:
      !isReady || index.isLoading || (index.isError && index.isFetching),
    isError: index.isError,
    onRetry: () => retry([index]),
    // An empty type would open a scope with nothing in it.
    scopes: toScopes(
      OBJECT_TYPES.filter(
        (type) => isIndexed(type) && (indexData?.rowCounts.get(type) ?? 0) > 0,
      ),
    ),
  });
  useRegisterCommandResults({
    id: "object-search",
    deferred: true,
    scopes: toScopes(
      OBJECT_TYPES.filter((type) => isListable(type) && hasRows(type)),
    ),
    results: listedScope
      ? [...(listingData ?? []), ...searchResults]
      : searchResults,
    isLoading:
      (!!listedScope && listing.isLoading) ||
      (isSearchable && (term !== query || serverSearch.isFetching)) ||
      failedSearches.some((failed) => failed.isFetching),
    isError: failedSearches.length > 0,
    onRetry: () => retry(failedSearches),
  });
};
